/**
 * Standard sentiment & intent labels returned by APIs (and normalized for UI).
 * Mobile apps can mirror Lucide icon names below for visual parity with web.
 */

export const STANDARD_SENTIMENTS = [
  'happy',
  'neutral',
  'worried',
  'upset',
  'angry',
  'frustrated',
  'confused',
  'urgent',
  'other',
];

export const STANDARD_INTENTS = [
  'emergency',
  'booking',
  'callback',
  'enquiry',
  'complaint',
  'reschedule',
  'cancellation',
  'quote',
  'billing',
  'other',
];

const slug = (raw) =>
  String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

/** Maps API synonyms / variants to a STANDARD_SENTIMENTS key. */
const SENTIMENT_ALIASES = {
  positive: 'happy',
  satisfied: 'happy',
  good: 'happy',
  negative: 'upset',
  bad: 'upset',
  mixed: 'neutral',
  unsure: 'confused',
  unknown: 'other',
};

/** Maps API synonyms / variants to a STANDARD_INTENTS key. */
const INTENT_ALIASES = {
  schedule: 'booking',
  scheduling: 'booking',
  appointment: 'booking',
  book: 'booking',
  pricing: 'quote',
  general_inquiry: 'enquiry',
  inquiry: 'enquiry',
  question: 'enquiry',
  info: 'enquiry',
  information: 'enquiry',
  complain: 'complaint',
  reschedule_appointment: 'reschedule',
  cancel: 'cancellation',
  cancelation: 'cancellation',
  estimate: 'quote',
  invoice: 'billing',
  payment: 'billing',
  payments: 'billing',
  follow_up: 'callback',
  followup: 'callback',
  call_back: 'callback',
};

export function normalizeSentiment(raw) {
  const key = slug(raw);
  if (!key) return 'other';
  if (STANDARD_SENTIMENTS.includes(key)) return key;
  if (SENTIMENT_ALIASES[key]) return SENTIMENT_ALIASES[key];
  return 'other';
}

export function normalizeIntent(raw) {
  const key = slug(raw);
  if (!key) return 'other';
  if (STANDARD_INTENTS.includes(key)) return key;
  if (INTENT_ALIASES[key]) return INTENT_ALIASES[key];
  return 'other';
}

/**
 * Lucide React icon component names (v0.507+) — use the same names in mobile
 * if you bundle Lucide, or pick equivalent glyphs in your design system.
 */
export const SENTIMENT_LUCIDE_ICONS = {
  happy: 'Smile',
  neutral: 'Meh',
  worried: 'AlertCircle',
  upset: 'Frown',
  angry: 'Angry',
  frustrated: 'Zap',
  confused: 'HelpCircle',
  urgent: 'AlarmClock',
  other: 'Ellipsis',
};

export const INTENT_LUCIDE_ICONS = {
  emergency: 'Siren',
  booking: 'CalendarCheck',
  callback: 'PhoneForwarded',
  enquiry: 'MessageCircleQuestion',
  complaint: 'ThumbsDown',
  reschedule: 'CalendarClock',
  cancellation: 'CalendarX',
  quote: 'FileText',
  billing: 'Receipt',
  other: 'Ellipsis',
};
